import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Plus, Trash2, RefreshCw, Image as ImageIcon, Shuffle, ArrowLeftRight, Info, ChevronLeft, ChevronRight, Users, UserPlus } from 'lucide-react';
import Modal from '../../components/Modal';
import ContactForm from '../../components/ContactForm';
import TypeaheadSelect from '../../components/TypeaheadSelect';
import ZoomControl, { useZoom, clampZoom } from '../../components/ZoomControl';
import {
  getInfluenceMap,
  getInfluenceMapSuggestions,
  hideInfluenceMapSuggestion,
  updateInfluenceMap,
  addMapPerson,
  updateMapPerson,
  removeMapPerson,
  promoteMapPerson,
  addMapLink,
  updateMapLink,
  removeMapLink,
  renameMapGroup,
  deleteMapRow,
  setMapRowPosition,
  updateContact,
  createContact,
} from '../../api';
import InfluenceCanvas, {
  positionOf,
  arrowAnchors,
  curvePath,
  rowLabelGutter,
  rowTopOf,
  rowOffsetOf,
  CARD_W,
  CARD_H,
  COL_W,
  COL_GAP,
  COL_HEAD_H,
  CANVAS_PADDING,
  ROW_H,
  STATUS_COLORS,
  ARROW_COLORS,
  linkColorKey,
  UNASSIGNED_KEY,
} from './InfluenceCanvas';
import './AccountInfluenceMapTab.css';

// ─── Constants ────────────────────────────────────────────────────────────

const INFLUENCE_OPTIONS = [
  { value: '', label: '— not set —' },
  { value: 'DM', label: 'DM · Decision Maker' },
  { value: 'INF', label: 'INF · Influences DM' },
  { value: 'IM', label: 'IM · Implementer' },
  { value: 'CO', label: 'CO · Coach / Consultant' },
  { value: 'NI', label: 'NI · No Influence' },
];

const SOCIAL_OPTIONS = [
  { value: '', label: '— not set —' },
  { value: 'DR', label: 'DR · Driver' },
  { value: 'EX', label: 'EX · Expressive' },
  { value: 'AY', label: 'AY · Analytic' },
  { value: 'AM', label: 'AM · Amiable' },
  { value: 'UN', label: 'UN · Unknown' },
];

const STATUS_OPTIONS = [
  { value: '', label: '— not set —' },
  { value: 'green', label: 'Good, established trust' },
  { value: 'yellow', label: 'Met, need to establish trust' },
  { value: 'red', label: 'Limited or no relationship' },
];

// ─── PNG export helper ────────────────────────────────────────────────────

// Build a self-contained SVG of the current map state. The browser will
// rasterize it to a canvas, then we save as PNG. Everything needed for
// rendering must be inline — no external assets, no CSS classes.
// Build the legend SVG block. Returns { svg, height } so the caller
// can size the overall output to fit both map and legend side by side.
function legendSvg(x, y, w) {
  const F = 'font-family="Segoe UI, Arial, sans-serif"';
  const pad = 14;
  let cy = y + pad;
  let content = '';

  // Title
  content += `<text x="${x + pad}" y="${cy + 12}" ${F} font-size="14" font-weight="700" fill="#1e293b">Legend</text>`;
  cy += 22;
  content += `<line x1="${x + pad}" y1="${cy}" x2="${x + w - pad}" y2="${cy}" stroke="#e2e8f0"/>`;
  cy += 8;

  const section = (title) => {
    cy += 6;
    content += `<text x="${x + pad}" y="${cy + 11}" ${F} font-size="12.5" font-weight="700" fill="#1e293b">${escapeXml(title)}</text>`;
    cy += 20;
  };
  const swatchRow = (color, label) => {
    content += `<rect x="${x + pad}" y="${cy + 1}" width="14" height="12" rx="3" fill="${color}" stroke="rgba(0,0,0,0.15)"/>`;
    content += `<text x="${x + pad + 22}" y="${cy + 11}" ${F} font-size="12" fill="#475569">${escapeXml(label)}</text>`;
    cy += 18;
  };
  const codeRow = (code, label) => {
    content += `<text x="${x + pad}" y="${cy + 11}" ${F} font-size="12" font-weight="700" fill="#1e293b">${escapeXml(code)}</text>`;
    content += `<text x="${x + pad + 50}" y="${cy + 11}" ${F} font-size="12" fill="#475569">${escapeXml(label)}</text>`;
    cy += 18;
  };

  section('Relationship Status');
  swatchRow(STATUS_COLORS.green, 'Good, established trust');
  swatchRow(STATUS_COLORS.yellow, 'Met, need to establish trust');
  swatchRow(STATUS_COLORS.red, 'Limited or no relationship');

  section('Disposition (number on card)');
  codeRow('+2…+5', 'Supporter / advocate');
  codeRow('−1…+1', 'Neutral / unknown');
  codeRow('−2…−5', 'Detractor / action req.');

  section('Influence Role');
  codeRow('DM', 'Decision Maker');
  codeRow('INF', 'Influences DM');
  codeRow('IM', 'Implementer');
  codeRow('CO', 'Coach / consultant');
  codeRow('NI', 'No influence');

  section('Social Style');
  codeRow('DR', 'Driver (direct/closed)');
  codeRow('EX', 'Expressive (direct/open)');
  codeRow('AY', 'Analytic (indirect/closed)');
  codeRow('AM', 'Amiable (indirect/open)');
  codeRow('UN', 'Unknown');

  const height = cy - y + pad;
  const box = `<rect x="${x}" y="${y}" width="${w}" height="${height}" rx="8" fill="#ffffff" stroke="#e2e8f0"/>`;
  return { svg: box + content, height };
}

function buildExportSvg(map, accountName) {
  const gutter = rowLabelGutter(map);
  const rowLabels = map?.rows || [];
  const F = 'font-family="Segoe UI, Arial, sans-serif"';

  // Export uses LABELLED columns only — the in-app "Unassigned"
  // staging area is workspace-only and should not appear in slides.
  const exportColumns = (map?.groups || []);
  const exportPersonIds = new Set();
  const people = (map?.people || []).filter(p => {
    const ok = !!p.group_name && exportColumns.includes(p.group_name);
    if (ok) exportPersonIds.add(p.id);
    return ok;
  });
  const links = (map?.links || []).filter(l =>
    exportPersonIds.has(l.source_person_id) && exportPersonIds.has(l.target_person_id)
  );

  // Compute the map content area's dimensions based on the filtered content.
  // Rows are free-positioned, so clear both the lowest card and the lowest
  // row label.
  const maxRow = people.reduce((m, p) => Math.max(m, p.row_index || 0), -1);
  const maxCardBottom = (maxRow + 1) * ROW_H;
  const maxRowBottom = rowLabels.reduce((m, _r, i) => Math.max(m, rowOffsetOf(map, i) + ROW_H), 0);
  const gridH = Math.max(maxCardBottom, maxRowBottom, ROW_H);
  const colCount = Math.max(exportColumns.length, 1);
  const mapAreaW = CANVAS_PADDING * 2 + gutter + colCount * (COL_W + COL_GAP) - COL_GAP;
  const mapAreaH = CANVAS_PADDING * 2 + COL_HEAD_H + gridH + ROW_H;

  // Layout: title band on top, map on left, legend on right.
  // For typical 3-5 swimlanes the natural aspect lands close enough
  // to 16:9 for a PowerPoint slide; users resize within PPT anyway.
  const titleH = 56;
  const legendW = 280;
  const legendGap = 28;
  const outerMargin = 20;

  // Render the legend once to get its actual height so we can
  // compute total SVG height.
  const legend = legendSvg(
    outerMargin + mapAreaW + legendGap,
    titleH + outerMargin,
    legendW
  );

  const totalW = outerMargin + mapAreaW + legendGap + legendW + outerMargin;
  const totalH = titleH + outerMargin + Math.max(mapAreaH, legend.height) + outerMargin;

  let svg = '';
  // Title band — CRM accent blue. Subtitle = account name.
  svg += `<rect x="0" y="0" width="${totalW}" height="${titleH}" fill="#3b82f6"/>`;
  svg += `<text x="${outerMargin}" y="34" ${F} font-size="22" font-weight="700" fill="#ffffff">Influence Map</text>`;
  svg += `<text x="${outerMargin + 200}" y="34" ${F} font-size="14" fill="rgba(255,255,255,0.88)">· ${escapeXml(accountName || '')}</text>`;

  // Arrow markers (one per selectable color)
  svg += '<defs>';
  Object.entries(ARROW_COLORS).forEach(([key, hex]) => {
    svg += `<marker id="im-export-arrow-${key}" markerWidth="11" markerHeight="11" refX="9" refY="5" orient="auto-start-reverse" markerUnits="userSpaceOnUse"><path d="M0,0 L10,5 L0,10 Z" fill="${hex}"/></marker>`;
  });
  svg += '</defs>';

  // Map content shifted right by outerMargin and down by titleH+outerMargin
  svg += `<g transform="translate(${outerMargin},${titleH + outerMargin})">`;

  // Row labels in the left gutter (at their free vertical positions)
  rowLabels.forEach((label, ri) => {
    const ly = rowTopOf(map, ri) + CARD_H / 2 + 4;
    svg += `<text x="${CANVAS_PADDING + gutter - 12}" y="${ly}" ${F} font-size="12.5" font-weight="700" fill="#64748b" text-anchor="end">${escapeXml(label)}</text>`;
  });

  // Column backgrounds + headers
  exportColumns.forEach((col, ci) => {
    const cx = CANVAS_PADDING + gutter + ci * (COL_W + COL_GAP);
    svg += `<rect x="${cx}" y="${CANVAS_PADDING + COL_HEAD_H - 8}" width="${COL_W}" height="${mapAreaH - CANVAS_PADDING - COL_HEAD_H}" rx="10" fill="rgba(255,255,255,0.7)" stroke="#e2e8f0"/>`;
    svg += `<text x="${cx + COL_W / 2}" y="${CANVAS_PADDING + 22}" ${F} font-size="15" font-weight="700" fill="#1e293b" text-anchor="middle">${escapeXml(col)}</text>`;
  });

  // Helper: resolve a link's two endpoints, or null if either is filtered out.
  const linkGeometry = (link) => {
    const srcPerson = people.find(p => p.id === link.source_person_id);
    const tgtPerson = people.find(p => p.id === link.target_person_id);
    if (!srcPerson || !tgtPerson) return null;
    const srcPos = positionOf(srcPerson, exportColumns, gutter);
    const tgtPos = positionOf(tgtPerson, exportColumns, gutter);
    return { ...arrowAnchors(srcPos, tgtPos), srcPos, tgtPos, link };
  };

  // Arrow LINES (no heads) — drawn behind the cards so the body passes
  // behind any card the line isn't connected to.
  links.forEach(link => {
    const geo = linkGeometry(link);
    if (!geo) return;
    const { sx, sy, tx, ty } = geo;
    const sw = (link.strength || 2) * 1.4 + 0.5;
    const ck = linkColorKey(link);
    svg += `<path d="${curvePath(sx, sy, tx, ty, !!link.curve_flipped)}" stroke="${ARROW_COLORS[ck]}" stroke-width="${sw}" fill="none"/>`;
  });

  // Cards (filtered)
  people.forEach(person => {
    const pos = positionOf(person, exportColumns, gutter);
    svg += cardSvg(person, pos);
  });

  // Arrow overlay — drawn in front of the cards. The line is clipped to the
  // two connected cards so the inset portion shows on top at both ends; the
  // arrowheads are unclipped so the point extends past the card edge.
  links.forEach(link => {
    const geo = linkGeometry(link);
    if (!geo) return;
    const { sx, sy, tx, ty, srcPos, tgtPos } = geo;
    const sw = (link.strength || 2) * 1.4 + 0.5;
    const ck = linkColorKey(link);
    const d = curvePath(sx, sy, tx, ty, !!link.curve_flipped);
    const clipId = `im-export-clip-${link.id}`;
    svg += `<clipPath id="${clipId}"><rect x="${srcPos.x}" y="${srcPos.y}" width="${CARD_W}" height="${CARD_H}"/><rect x="${tgtPos.x}" y="${tgtPos.y}" width="${CARD_W}" height="${CARD_H}"/></clipPath>`;
    svg += `<path d="${d}" stroke="${ARROW_COLORS[ck]}" stroke-width="${sw}" fill="none" clip-path="url(#${clipId})"/>`;
    const startMarker = link.bidirectional ? ` marker-start="url(#im-export-arrow-${ck})"` : '';
    svg += `<path d="${d}" stroke="transparent" stroke-width="${sw}" fill="none"${startMarker} marker-end="url(#im-export-arrow-${ck})"/>`;
  });

  svg += `</g>`;

  // Legend (positioned absolutely in the SVG, not inside the map group)
  svg += legend.svg;

  const svgString = `<svg xmlns="http://www.w3.org/2000/svg" width="${totalW}" height="${totalH}" viewBox="0 0 ${totalW} ${totalH}"><rect width="${totalW}" height="${totalH}" fill="#f8fafc"/>${svg}</svg>`;
  return { svg: svgString, width: totalW, height: totalH };
}

function cardSvg(person, pos) {
  const F = 'font-family="Segoe UI, Arial, sans-serif"';
  const { x, y } = pos;
  const statusColor = person.relationship_status
    ? STATUS_COLORS[person.relationship_status] || '#94a3b8'
    : '#94a3b8';
  const statusTextColor = person.relationship_status === 'yellow' ? '#5a4b00' : '#ffffff';
  const dispDisplay = person.disposition == null
    ? '·'
    : (person.disposition > 0 ? `+${person.disposition}` : String(person.disposition));
  const cellY = y + CARD_H - 24;
  const cw3 = CARD_W / 3;
  let g = '';
  g += `<rect x="${x}" y="${y}" width="${CARD_W}" height="${CARD_H}" rx="4" fill="#ffffff" stroke="#9aa6b4"/>`;
  g += `<text x="${x + CARD_W / 2}" y="${y + 19}" text-anchor="middle" ${F} font-size="12.5" font-weight="700" fill="#1f2933">${escapeXml(truncate(person.name, 24))}</text>`;
  if (person.title) {
    // Wrap the title across up to 3 lines so longer titles aren't clipped.
    // Three 12px-pitch lines from y+33 reach baseline y+57 — descenders land
    // ~y+60, clearing the stats divider at y+66 (CARD_H - 24).
    const titleLines = wrapText(person.title, 30, 3);
    titleLines.forEach((line, i) => {
      g += `<text x="${x + CARD_W / 2}" y="${y + 33 + i * 12}" text-anchor="middle" ${F} font-size="10.5" font-style="italic" fill="#45505f">${escapeXml(line)}</text>`;
    });
  }
  g += `<line x1="${x}" y1="${cellY}" x2="${x + CARD_W}" y2="${cellY}" stroke="#9aa6b4"/>`;
  g += `<rect x="${x}" y="${cellY}" width="${cw3}" height="24" fill="${statusColor}"/>`;
  g += `<text x="${x + cw3 / 2}" y="${cellY + 16}" text-anchor="middle" ${F} font-size="11" font-weight="700" fill="${statusTextColor}">${escapeXml(dispDisplay)}</text>`;
  g += `<text x="${x + cw3 * 1.5}" y="${cellY + 16}" text-anchor="middle" ${F} font-size="11" font-weight="700" fill="#1f2933">${escapeXml(person.influence_role || '·')}</text>`;
  g += `<text x="${x + cw3 * 2.5}" y="${cellY + 16}" text-anchor="middle" ${F} font-size="11" font-weight="700" fill="#1f2933">${escapeXml(person.social_style || '·')}</text>`;
  g += `<line x1="${x + cw3}" y1="${cellY}" x2="${x + cw3}" y2="${y + CARD_H}" stroke="#9aa6b4"/>`;
  g += `<line x1="${x + cw3 * 2}" y1="${cellY}" x2="${x + cw3 * 2}" y2="${y + CARD_H}" stroke="#9aa6b4"/>`;
  return g;
}

function truncate(str, max) {
  const s = String(str || '');
  return s.length > max ? s.slice(0, max - 1) + '…' : s;
}

// Greedy word-wrap into at most `maxLines` lines of ~`maxChars` each.
// The last line absorbs any remainder and is ellipsised if it overflows.
function wrapText(str, maxChars, maxLines) {
  const words = String(str || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const lines = [];
  let cur = '';
  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    const candidate = cur ? `${cur} ${word}` : word;
    // On the final allowed line, keep appending all remaining words so nothing is dropped.
    if (candidate.length <= maxChars || !cur || lines.length === maxLines - 1) {
      cur = candidate;
    } else {
      lines.push(cur);
      cur = word;
    }
  }
  if (cur) lines.push(cur);
  const last = lines.length - 1;
  if (lines[last].length > maxChars) lines[last] = truncate(lines[last], maxChars);
  return lines;
}

function escapeXml(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

// Encode SVG → PNG via canvas, trigger download.
function downloadMapAsPng(map, accountName) {
  const { svg, width, height } = buildExportSvg(map, accountName);

  const img = new Image();
  img.onload = () => {
    // 2x for crisp slides — PowerPoint will resample, so a generous
    // source resolution makes the PNG hold up under scaling.
    const scale = 2;
    const cv = document.createElement('canvas');
    cv.width = Math.round(width * scale);
    cv.height = Math.round(height * scale);
    const ctx = cv.getContext('2d');
    ctx.scale(scale, scale);
    ctx.drawImage(img, 0, 0);
    cv.toBlob(blob => {
      if (!blob) { alert('Could not generate image.'); return; }
      const safe = (accountName || 'account').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `influence-map-${safe}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    }, 'image/png');
  };
  img.onerror = () => alert('Could not render the map image.');
  img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
}

// ─── Tab component ────────────────────────────────────────────────────────

function AccountInfluenceMapTab({
  account,
  canDo,
  reloadAccount,
  leadSources = [],
  repFirms = [],
  users = [],
}) {
  // Window zoom for a map that has outgrown the card; remembered per browser.
  const [zoom, setZoom] = useZoom('crm_zoom_influence_map');
  const boardRef = useRef(null);
  const fitZoom = () => {
    // The drawing's own width is its inline style; offsetWidth would report
    // the canvas stretched to the window. Fit only ever shrinks.
    const board = boardRef.current; const canvas = board?.firstElementChild;
    const drawingWidth = canvas ? (parseFloat(canvas.style.width) || canvas.offsetWidth) : 0;
    if (!board || !drawingWidth) return;
    setZoom(clampZoom(Math.min(1, (board.clientWidth - 2) / drawingWidth)));
  };
  const [map, setMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Distinct swimlane/row names used across all maps — fed to the toolbar
  // "Add swimlane / Add row" quick-pick dropdowns. `addResetKey` is bumped
  // after each add so the controlled TypeaheadSelect remounts and clears.
  const [suggestions, setSuggestions] = useState({ groups: [], rows: [], hidden: { groups: [], rows: [] } });
  const [addResetKey, setAddResetKey] = useState(0);

  // Edit-person modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPerson, setEditingPerson] = useState(null);
  const [personForm, setPersonForm] = useState({});
  // Which tab is active in the edit modal: 'map' or 'contact'
  const [activeModalTab, setActiveModalTab] = useState('map');
  // Mirror of the linked contact for the Contact tab. Populated from
  // account.contacts when the modal opens for a contact-linked person.
  const [contactDraft, setContactDraft] = useState(null);

  // Add-contact modal state. "Add contact" opens the shared ContactForm
  // and, on save, creates a real CRM contact on this account and places
  // it on the map — replacing the old name-only ad-hoc placeholder flow.
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [newContactForm, setNewContactForm] = useState(null);

  // Arrow editor modal state
  const [editingLink, setEditingLink] = useState(null);

  // Filter / search state
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterInfluence, setFilterInfluence] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  // On small screens, start with the side panels collapsed so the canvas
  // gets the full width — they're one tap away on the rails.
  const isMobile = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(max-width: 768px)').matches;

  // Legend panel visibility — default on so the codes (DM/INF/IM/CO/NI,
  // DR/EX/AY/AM/UN, the status colors) are explained without the user
  // having to hunt for a help button. Collapsed by default on mobile.
  const [showLegend, setShowLegend] = useState(!isMobile);

  // Whether the left "Account contacts" drawer is minimized to a thin
  // rail. Lets the user reclaim horizontal space for the canvas.
  const [drawerCollapsed, setDrawerCollapsed] = useState(isMobile);

  const canEdit = canDo('contacts', 'edit');

  // ── Load ─────────────────────────────────────────────────────────────
  const loadMap = useCallback(async () => {
    try {
      const res = await getInfluenceMap(account.id);
      setMap(res.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load influence map');
    } finally {
      setLoading(false);
    }
  }, [account.id]);

  useEffect(() => { loadMap(); }, [loadMap]);

  const loadSuggestions = useCallback(async () => {
    try {
      const res = await getInfluenceMapSuggestions();
      setSuggestions({
        groups: res.data?.groups || [],
        rows: res.data?.rows || [],
        hidden: {
          groups: res.data?.hidden?.groups || [],
          rows: res.data?.hidden?.rows || [],
        },
      });
    } catch (err) {
      // Non-fatal — the dropdowns just fall back to "add new" only.
    }
  }, []);

  // Remove a misspelled/unwanted name from the swimlane/row quick-pick. Hides
  // it globally (server-side) and optimistically drops it from local state so
  // it disappears from the dropdown immediately. Does NOT touch lanes/rows
  // already placed on a map — only the suggestion vocabulary.
  const hideSuggestion = useCallback(async (type, name) => {
    setSuggestions(prev => {
      const key = type === 'group' ? 'groups' : 'rows';
      return {
        ...prev,
        [key]: prev[key].filter(n => n !== name),
        hidden: { ...prev.hidden, [key]: [...prev.hidden[key], name] },
      };
    });
    try {
      await hideInfluenceMapSuggestion(type, name);
    } catch (err) {
      // Re-sync from the server if the hide failed so state stays accurate.
      loadSuggestions();
    }
  }, [loadSuggestions]);

  useEffect(() => { loadSuggestions(); }, [loadSuggestions]);

  // ── Derived: off-map contacts (drawer) ──────────────────────────────
  const onMapContactIds = useMemo(() => {
    if (!map) return new Set();
    return new Set(map.people.filter(p => p.contact_id).map(p => p.contact_id));
  }, [map]);

  const offMapContacts = useMemo(() => {
    const contacts = account?.contacts || [];
    return contacts.filter(c => c.active !== 0 && !onMapContactIds.has(c.id));
  }, [account?.contacts, onMapContactIds]);

  // ── Derived: which people match the current filters ─────────────────
  const dimmedIds = useMemo(() => {
    if (!map) return new Set();
    const q = searchText.trim().toLowerCase();
    const dimmed = new Set();
    for (const p of map.people) {
      let matches = true;
      if (q) {
        const hay = `${p.name || ''} ${p.title || ''}`.toLowerCase();
        if (!hay.includes(q)) matches = false;
      }
      if (matches && filterStatus && p.relationship_status !== filterStatus) {
        matches = false;
      }
      if (matches && filterInfluence && p.influence_role !== filterInfluence) {
        matches = false;
      }
      if (matches && filterGroup) {
        const g = p.group_name || UNASSIGNED_KEY;
        if (g !== filterGroup) matches = false;
      }
      if (!matches) dimmed.add(p.id);
    }
    return dimmed;
  }, [map, searchText, filterStatus, filterInfluence, filterGroup]);

  const matchCount = map ? map.people.length - dimmedIds.size : 0;

  // ── Person CRUD callbacks ────────────────────────────────────────────

  // Find the next free row in the Unassigned column so a newly-placed
  // contact never lands on top of an existing card.
  const nextFreeUnassignedRow = useCallback(() => {
    if (!map) return 0;
    const unassigned = map.people.filter(p => !p.group_name);
    if (unassigned.length === 0) return 0;
    const maxRow = unassigned.reduce(
      (m, p) => Math.max(m, p.row_index || 0),
      -1
    );
    return maxRow + 1;
  }, [map]);

  const placeContact = async (contact) => {
    if (!canEdit || !map) return;
    setSaving(true);
    try {
      const res = await addMapPerson(map.id, {
        contact_id: contact.id,
        group_name: null,
        row_index: nextFreeUnassignedRow(),
      });
      setMap(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to place contact');
    } finally {
      setSaving(false);
    }
  };

  const handlePersonClick = (personId) => {
    const person = map?.people.find(p => p.id === personId);
    if (!person) return;
    openEditModal(person);
  };

  const handleMovePerson = useCallback(async (personId, partial) => {
    if (!map) return;
    setSaving(true);
    try {
      const res = await updateMapPerson(map.id, personId, partial);
      setMap(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to move card');
    } finally {
      setSaving(false);
    }
  }, [map]);

  const openEditModal = (person) => {
    setEditingPerson(person);
    setActiveModalTab('map');
    setPersonForm({
      name: person.name || '',
      title: person.title || '',
      group_name: person.group_name || '',
      row_index: person.row_index == null ? '' : person.row_index,
      influence_role: person.influence_role || '',
      social_style: person.social_style || '',
      relationship_status: person.relationship_status || '',
      disposition: person.disposition == null ? '' : person.disposition,
      notes: person.notes || '',
      _has_influence_override: !!person.influence_role_override,
      _has_social_override: !!person.social_style_override,
    });
    // For linked contacts, seed the Contact tab with the live contact
    // record from account.contacts. The Contact tab uses the same
    // shared ContactForm component as the Contacts tab modal.
    if (person.contact_id) {
      const contact = (account?.contacts || []).find(c => c.id === person.contact_id);
      if (contact) {
        const nameParts = (contact.name || '').trim().split(/\s+/);
        setContactDraft({
          first_name: contact.first_name || nameParts[0] || '',
          last_name: contact.last_name || nameParts.slice(1).join(' ') || '',
          email: contact.email || '',
          phone: contact.phone || '',
          job_title: contact.job_title || '',
          linkedin_url: contact.linkedin_url || '',
          active: contact.active !== undefined ? !!contact.active : true,
          market_to: contact.market_to !== undefined ? !!contact.market_to : true,
          lead_source_id: contact.lead_source_id || '',
          rep_firm_id: contact.rep_firm_id || '',
          owner_id: contact.owner_id || '',
          notes: contact.notes || '',
          influence_role: contact.influence_role || '',
          social_style: contact.social_style || '',
          relationship_status: contact.relationship_status || '',
          disposition: contact.disposition == null ? '' : contact.disposition,
          account_id: contact.account_id,
        });
      } else {
        setContactDraft(null);
      }
    } else {
      setContactDraft(null);
    }
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingPerson(null);
    setPersonForm({});
    setContactDraft(null);
    setActiveModalTab('map');
  };

  // Tab 1 (Map) save — writes map-only fields + write-through fields
  // (relationship_status, disposition) via the influence-map endpoint.
  const savePerson = async (e) => {
    e.preventDefault();
    if (!editingPerson) return;
    setSaving(true);
    try {
      const payload = {
        group_name: personForm.group_name || null,
        notes: personForm.notes || null,
        // Only write row_index when a row is chosen — clearing the field
        // leaves the card's current vertical position untouched.
        ...(personForm.row_index === '' || personForm.row_index == null
          ? {}
          : { row_index: personForm.row_index, x: null, y: null }),
        influence_role: personForm.influence_role || null,
        social_style: personForm.social_style || null,
        relationship_status: personForm.relationship_status || null,
        disposition: personForm.disposition === '' ? null : parseInt(personForm.disposition, 10),
      };
      if (!editingPerson.contact_id) {
        payload.name = personForm.name;
        payload.title = personForm.title || null;
      }
      const res = await updateMapPerson(map.id, editingPerson.id, payload);
      setMap(res.data);
      if (editingPerson.contact_id && reloadAccount) reloadAccount();
      closeEditModal();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save person');
    } finally {
      setSaving(false);
    }
  };

  // Tab 2 (Contact) save — writes the linked contact record via the
  // /api/contacts endpoint. After save we reload the parent account so
  // the Contacts tab sees the change AND reload the map so any field
  // shown on the card (name/title from contact, status/disposition) is
  // fresh.
  const saveContact = async (e) => {
    e.preventDefault();
    if (!editingPerson || !editingPerson.contact_id || !contactDraft) return;
    setSaving(true);
    try {
      await updateContact(editingPerson.contact_id, contactDraft);
      await loadMap();
      if (reloadAccount) reloadAccount();
      closeEditModal();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  const clearOverride = async (field) => {
    if (!editingPerson) return;
    setSaving(true);
    try {
      const payload = field === 'influence_role'
        ? { influence_role: null }
        : { social_style: null };
      const res = await updateMapPerson(map.id, editingPerson.id, payload);
      setMap(res.data);
      const fresh = res.data.people.find(p => p.id === editingPerson.id);
      if (fresh) openEditModal(fresh);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to clear override');
    } finally {
      setSaving(false);
    }
  };

  const removeFromMap = async (person) => {
    if (!canEdit || !map) return;
    if (!window.confirm(`Remove ${person.name} from this map? The CRM contact will not be deleted.`)) return;
    setSaving(true);
    try {
      const res = await removeMapPerson(map.id, person.id);
      setMap(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove person');
    } finally {
      setSaving(false);
    }
  };

  const promoteAdHoc = async () => {
    if (!editingPerson || editingPerson.contact_id) return;
    setSaving(true);
    try {
      const res = await promoteMapPerson(map.id, editingPerson.id, {});
      setMap(res.data);
      if (reloadAccount) reloadAccount();
      closeEditModal();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to promote to CRM contact');
    } finally {
      setSaving(false);
    }
  };

  // Blank ContactForm draft for creating a brand-new account contact.
  const blankContactForm = () => ({
    first_name: '', last_name: '', email: '', phone: '', job_title: '',
    linkedin_url: '', active: true, market_to: true,
    lead_source_id: '', rep_firm_id: '', owner_id: '', notes: '',
    influence_role: '', social_style: '', relationship_status: '', disposition: '',
  });

  const openAddContact = () => {
    setNewContactForm(blankContactForm());
    setShowAddContactModal(true);
  };

  const closeAddContact = () => {
    setShowAddContactModal(false);
    setNewContactForm(null);
  };

  // Create a real CRM contact on this account, then place it on the map.
  const saveNewContact = async (e) => {
    e.preventDefault();
    if (!newContactForm || !account?.id) return;
    if (!newContactForm.first_name?.trim()) {
      setError('First name is required');
      return;
    }
    setSaving(true);
    try {
      const res = await createContact({ ...newContactForm, account_id: account.id });
      const contact = res.data;
      const mapRes = await addMapPerson(map.id, {
        contact_id: contact.id,
        group_name: null,
        row_index: nextFreeUnassignedRow(),
      });
      setMap(mapRes.data);
      if (reloadAccount) reloadAccount();
      closeAddContact();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add contact');
    } finally {
      setSaving(false);
    }
  };

  // ── Swimlane CRUD ────────────────────────────────────────────────────

  // Quick-add a swimlane by name from the toolbar dropdown — used both when
  // the user picks an existing name (reused from another map) and when they
  // type a brand-new one and choose "Add". Dedupes against this map.
  const addGroupByName = useCallback(async (rawName) => {
    const name = (rawName || '').trim();
    if (!name || !map || !canEdit) { setAddResetKey(k => k + 1); return; }
    const existing = map.groups || [];
    if (existing.includes(name)) { setAddResetKey(k => k + 1); return; }
    setSaving(true);
    try {
      const res = await updateInfluenceMap(map.id, { groups: [...existing, name] });
      setMap(res.data);
      loadSuggestions();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add swimlane');
    } finally {
      setSaving(false);
      setAddResetKey(k => k + 1);
    }
  }, [map, canEdit, loadSuggestions]);

  const handleRenameGroup = useCallback(async (from, to) => {
    if (!map || !canEdit) return;
    setSaving(true);
    try {
      const res = await renameMapGroup(map.id, from, to);
      setMap(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to rename swimlane');
    } finally {
      setSaving(false);
    }
  }, [map, canEdit]);

  const handleDeleteGroup = useCallback(async (groupName) => {
    if (!map || !canEdit) return;
    if (!window.confirm(`Delete the "${groupName}" swimlane? Cards in it will move to Unassigned.`)) return;
    const remaining = (map.groups || []).filter(g => g !== groupName);
    setSaving(true);
    try {
      const res = await updateInfluenceMap(map.id, { groups: remaining });
      setMap(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete swimlane');
    } finally {
      setSaving(false);
    }
  }, [map, canEdit]);

  // Move a swimlane to an absolute target position (drag-to-reorder).
  // targetIndex is the desired FINAL 0-based position of `groupName` among
  // the named groups; "Unassigned" isn't in map.groups so it always stays
  // pinned at the far right.
  const handleReorderGroupToIndex = useCallback(async (groupName, targetIndex) => {
    if (!map || !canEdit) return;
    const groups = [...(map.groups || [])];
    const from = groups.indexOf(groupName);
    if (from < 0) return;
    groups.splice(from, 1); // remove first
    const clamped = Math.max(0, Math.min(groups.length, targetIndex));
    groups.splice(clamped, 0, groupName); // insert at desired final position
    // Skip the network round-trip if nothing actually changed.
    const prevGroups = map.groups || [];
    const unchanged = prevGroups.length === groups.length
      && prevGroups.every((g, i) => g === groups[i]);
    if (unchanged) return;
    // Optimistic reorder so the lanes settle into place instantly on drop
    // (no flicker waiting for the round-trip); revert if the save fails.
    setMap(m => ({ ...m, groups }));
    setSaving(true);
    try {
      const res = await updateInfluenceMap(map.id, { groups });
      setMap(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reorder swimlane');
      setMap(m => ({ ...m, groups: prevGroups }));
    } finally {
      setSaving(false);
    }
  }, [map, canEdit]);

  // Move a row label to a free vertical position (drag up/down, leave it where
  // dropped). Rows are decoupled from cards — NO card moves. `offset` is the
  // px distance below the first-row baseline. Optimistic update for no flicker.
  const handleMoveRowOffset = useCallback(async (index, offset) => {
    if (!map || !canEdit) return;
    const rows = map.rows || [];
    if (index < 0 || index >= rows.length) return;
    const prev = map;
    const nextRowY = [...(map.row_y || [])];
    while (nextRowY.length <= index) nextRowY.push(null);
    nextRowY[index] = offset;
    setMap(m => ({ ...m, row_y: nextRowY }));
    setSaving(true);
    try {
      const res = await setMapRowPosition(map.id, index, offset);
      setMap(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to move row');
      setMap(prev);
    } finally {
      setSaving(false);
    }
  }, [map, canEdit]);

  // ── Row CRUD ─────────────────────────────────────────────────────────

  // Quick-add a row by name from the toolbar dropdown (existing pick or
  // typed "Add new"). Dedupes against this map's current rows.
  const addRowByName = useCallback(async (rawName) => {
    const name = (rawName || '').trim();
    if (!name || !map || !canEdit) { setAddResetKey(k => k + 1); return; }
    const existing = map.rows || [];
    if (existing.includes(name)) { setAddResetKey(k => k + 1); return; }
    setSaving(true);
    try {
      const res = await updateInfluenceMap(map.id, { rows: [...existing, name] });
      setMap(res.data);
      loadSuggestions();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add row');
    } finally {
      setSaving(false);
      setAddResetKey(k => k + 1);
    }
  }, [map, canEdit, loadSuggestions]);

  // Renaming a row is just a label change — no card positions move,
  // since rows are referenced by index, not by name. Single PUT.
  const handleRenameRow = useCallback(async (index, to) => {
    if (!map || !canEdit) return;
    const rows = [...(map.rows || [])];
    if (index < 0 || index >= rows.length) return;
    if (rows[index] === to) return;
    rows[index] = to;
    setSaving(true);
    try {
      const res = await updateInfluenceMap(map.id, { rows });
      setMap(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to rename row');
    } finally {
      setSaving(false);
    }
  }, [map, canEdit]);

  // Deleting a row just removes the label (rows are decoupled from cards now —
  // no card moves).
  const handleDeleteRow = useCallback(async (index) => {
    if (!map || !canEdit) return;
    const label = (map.rows || [])[index];
    if (!window.confirm(`Delete the "${label}" row label? Cards won't move.`)) return;
    setSaving(true);
    try {
      const res = await deleteMapRow(map.id, index);
      setMap(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete row');
    } finally {
      setSaving(false);
    }
  }, [map, canEdit]);

  // ── Inline add for the person-edit dropdowns ─────────────────────────
  // The Swimlane/Row selectors in the edit modal let you pick an existing
  // name or "Add new" inline (same pattern as the contact typeahead). These
  // add the name to the map and select it on the form without leaving the
  // modal. `typedName` is the text the user typed into the dropdown; we fall
  // back to a prompt if they clicked "Add new" with an empty field.

  const addSwimlaneInline = useCallback(async (typedName) => {
    if (!map || !canEdit) return;
    let name = (typedName || '').trim();
    if (!name) {
      name = (window.prompt('Name for the new swimlane:') || '').trim();
    }
    if (!name) return;
    const existing = map.groups || [];
    if (!existing.includes(name)) {
      setSaving(true);
      try {
        const res = await updateInfluenceMap(map.id, { groups: [...existing, name] });
        setMap(res.data);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to add swimlane');
        return;
      } finally {
        setSaving(false);
      }
    }
    setPersonForm(f => ({ ...f, group_name: name }));
  }, [map, canEdit]);

  const addRowInline = useCallback(async (typedName) => {
    if (!map || !canEdit) return;
    const existing = map.rows || [];
    let name = (typedName || '').trim();
    if (!name) {
      name = (window.prompt('Name for the new row:', `Row ${existing.length + 1}`) || '').trim();
    }
    if (!name) return;
    let index = existing.indexOf(name);
    if (index < 0) {
      setSaving(true);
      try {
        const res = await updateInfluenceMap(map.id, { rows: [...existing, name] });
        setMap(res.data);
        index = existing.length;
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to add row');
        return;
      } finally {
        setSaving(false);
      }
    }
    setPersonForm(f => ({ ...f, row_index: index }));
  }, [map, canEdit]);

  // ── Link CRUD ────────────────────────────────────────────────────────

  const handleCreateLink = useCallback(async (sourceId, targetId) => {
    if (!map) return;
    // De-dupe — if a link already exists in this direction, no-op.
    const existing = (map.links || []).find(l =>
      l.source_person_id === sourceId && l.target_person_id === targetId
    );
    if (existing) return;
    setSaving(true);
    try {
      const res = await addMapLink(map.id, {
        source_person_id: sourceId,
        target_person_id: targetId,
        strength: 2,
      });
      setMap(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add arrow');
    } finally {
      setSaving(false);
    }
  }, [map]);

  const handleSelectLink = useCallback((link) => {
    setEditingLink(link);
  }, []);

  const updateLinkStrength = async (strength) => {
    if (!editingLink || !map) return;
    setSaving(true);
    try {
      const res = await updateMapLink(map.id, editingLink.id, { strength });
      setMap(res.data);
      const updated = res.data.links.find(l => l.id === editingLink.id);
      if (updated) setEditingLink(updated);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update arrow');
    } finally {
      setSaving(false);
    }
  };

  const updateLinkColor = async (color) => {
    if (!editingLink || !map) return;
    setSaving(true);
    try {
      const res = await updateMapLink(map.id, editingLink.id, { color });
      setMap(res.data);
      const updated = res.data.links.find(l => l.id === editingLink.id);
      if (updated) setEditingLink(updated);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update arrow');
    } finally {
      setSaving(false);
    }
  };

  const updateLinkBidirectional = async (bidirectional) => {
    if (!editingLink || !map) return;
    setSaving(true);
    try {
      const res = await updateMapLink(map.id, editingLink.id, { bidirectional });
      setMap(res.data);
      const updated = res.data.links.find(l => l.id === editingLink.id);
      if (updated) setEditingLink(updated);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update arrow');
    } finally {
      setSaving(false);
    }
  };

  const updateLinkCurveFlipped = async (curve_flipped) => {
    if (!editingLink || !map) return;
    setSaving(true);
    try {
      const res = await updateMapLink(map.id, editingLink.id, { curve_flipped });
      setMap(res.data);
      const updated = res.data.links.find(l => l.id === editingLink.id);
      if (updated) setEditingLink(updated);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update arrow');
    } finally {
      setSaving(false);
    }
  };

  const flipLink = async () => {
    if (!editingLink || !map) return;
    setSaving(true);
    try {
      const res = await updateMapLink(map.id, editingLink.id, {
        source_person_id: editingLink.target_person_id,
        target_person_id: editingLink.source_person_id,
      });
      setMap(res.data);
      const updated = res.data.links.find(l => l.id === editingLink.id);
      if (updated) setEditingLink(updated);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to flip arrow');
    } finally {
      setSaving(false);
    }
  };

  const deleteLink = async () => {
    if (!editingLink || !map) return;
    if (!window.confirm('Delete this arrow?')) return;
    setSaving(true);
    try {
      const res = await removeMapLink(map.id, editingLink.id);
      setMap(res.data);
      setEditingLink(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete arrow');
    } finally {
      setSaving(false);
    }
  };

  // ── Auto-arrange ─────────────────────────────────────────────────────
  // Within each column, sort by current row_index then renumber 0,1,2,...
  // Issues one PATCH per moved card. Could be batched server-side later.
  const autoArrange = async () => {
    if (!map || !canEdit) return;
    setSaving(true);
    try {
      const byCol = new Map();
      for (const p of map.people) {
        const key = p.group_name || UNASSIGNED_KEY;
        if (!byCol.has(key)) byCol.set(key, []);
        byCol.get(key).push(p);
      }
      const updates = [];
      for (const [, list] of byCol) {
        list.sort((a, b) => (a.row_index || 0) - (b.row_index || 0) || a.id - b.id);
        list.forEach((p, idx) => {
          if ((p.row_index || 0) !== idx || p.x != null || p.y != null) {
            updates.push({ id: p.id, row_index: idx });
          }
        });
      }
      // Apply sequentially so server-side rendering is consistent
      let latest = map;
      for (const u of updates) {
        const res = await updateMapPerson(map.id, u.id, {
          row_index: u.row_index, x: null, y: null,
        });
        latest = res.data;
      }
      setMap(latest);
    } catch (err) {
      setError(err.response?.data?.error || 'Auto-arrange failed');
    } finally {
      setSaving(false);
    }
  };

  // ── PNG export ───────────────────────────────────────────────────────
  const exportPng = () => {
    if (!map) return;
    try {
      downloadMapAsPng(map, account?.name);
    } catch (err) {
      setError('Could not export image');
    }
  };

  // ── Render ───────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="card">
        <div className="loading-container"><div className="spinner"></div></div>
      </div>
    );
  }

  if (error && !map) {
    return <div className="alert alert-error">{error}</div>;
  }

  // For link editor display
  const linkSrc = editingLink ? (map.people.find(p => p.id === editingLink.source_person_id)) : null;
  const linkTgt = editingLink ? (map.people.find(p => p.id === editingLink.target_person_id)) : null;

  // Toolbar "+ Swimlane / + Row" suggestion lists: the union of names used on
  // THIS map and on every other map (from the suggestions endpoint), deduped
  // and sorted. A name added here is persisted and re-fetched, so it stays in
  // the dropdown for reuse on this map and other accounts.
  const byName = (a, b) => a.localeCompare(b);
  // Subtract globally-hidden names so a name removed from the lookup stays gone
  // even if it's still live on this map.
  const hiddenGroups = new Set(suggestions.hidden?.groups || []);
  const hiddenRows = new Set(suggestions.hidden?.rows || []);
  const swimlaneSuggestionItems = Array.from(
    new Set([...(suggestions.groups || []), ...(map?.groups || [])])
  ).filter(g => !hiddenGroups.has(g)).sort(byName).map(g => ({ id: g, name: g }));
  const rowSuggestionItems = Array.from(
    new Set([...(suggestions.rows || []), ...(map?.rows || [])])
  ).filter(r => !hiddenRows.has(r)).sort(byName).map(r => ({ id: r, name: r }));

  return (
    <div className="card im-map-card">
      <div className="card-header">
        <div>
          <h3 className="card-title">Influence Map</h3>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
            Drag cards between swimlanes. Hover a card and click <span style={{
              display: 'inline-block', width: 14, height: 14, borderRadius: 7,
              background: 'var(--accent-primary)', color: '#fff', fontSize: 10, fontWeight: 700,
              lineHeight: '14px', textAlign: 'center', verticalAlign: 'middle',
            }}>+</span> to draw an influence arrow.
          </p>
        </div>
        <div className="flex gap-2">
          <ZoomControl zoom={zoom} onChange={setZoom} onFit={fitZoom} label="Zoom the map" />
          <button className="btn btn-secondary btn-sm" onClick={exportPng} disabled={!map} title="Download as PNG for slides">
            <ImageIcon size={14} /> Export PNG
          </button>
          {canEdit && (
            <>
              <button className="btn btn-secondary btn-sm" onClick={autoArrange} disabled={saving}>
                <Shuffle size={14} /> Auto-arrange
              </button>
              <div className="im-toolbar-add" title="Pick a swimlane name used on another map, or type a new one">
                <TypeaheadSelect
                  key={`add-sw-${addResetKey}`}
                  id="im-add-swimlane"
                  placeholder="+ Swimlane…"
                  items={swimlaneSuggestionItems}
                  value=""
                  displayValue=""
                  onChange={(id, name) => addGroupByName(name)}
                  onAddNew={addGroupByName}
                  showAddNewOption={false}
                  prefixMatch
                  onRemoveItem={(item) => hideSuggestion('group', item.name)}
                  removeItemTitle="Remove this swimlane name from the lookup"
                  disabled={saving}
                />
              </div>
              <div className="im-toolbar-add" title="Pick a row name used on another map, or type a new one">
                <TypeaheadSelect
                  key={`add-row-${addResetKey}`}
                  id="im-add-row"
                  placeholder="+ Row…"
                  items={rowSuggestionItems}
                  value=""
                  displayValue=""
                  onChange={(id, name) => addRowByName(name)}
                  onAddNew={addRowByName}
                  showAddNewOption={false}
                  prefixMatch
                  onRemoveItem={(item) => hideSuggestion('row', item.name)}
                  removeItemTitle="Remove this row name from the lookup"
                  disabled={saving}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ margin: '0 16px' }}>{error}</div>}

      {/* ───── Filter toolbar ───── */}
      <div className="im-toolbar">
        <input
          type="search"
          className="form-input im-filter"
          placeholder="Search name or title…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 200 }}
        />
        <select
          className="form-select im-filter"
          value={filterGroup}
          onChange={(e) => setFilterGroup(e.target.value)}
          style={{ width: 150 }}
        >
          <option value="">All swimlanes</option>
          {(map?.groups || []).map(g => <option key={g} value={g}>{g}</option>)}
          <option value={UNASSIGNED_KEY}>Unassigned</option>
        </select>
        <select
          className="form-select im-filter"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{ width: 150 }}
        >
          <option value="">All status</option>
          <option value="green">Established trust</option>
          <option value="yellow">Need to build trust</option>
          <option value="red">Limited / none</option>
        </select>
        <select
          className="form-select im-filter"
          value={filterInfluence}
          onChange={(e) => setFilterInfluence(e.target.value)}
          style={{ width: 150 }}
        >
          <option value="">All influence</option>
          <option value="DM">DM · Decision Maker</option>
          <option value="INF">INF · Influences DM</option>
          <option value="IM">IM · Implementer</option>
          <option value="CO">CO · Coach</option>
          <option value="NI">NI · No Influence</option>
        </select>
        <div className="im-toolbar-spacer" />
        <span className="im-counts">
          {map?.people.length || 0} on map ·{' '}
          {(searchText || filterStatus || filterInfluence || filterGroup)
            ? `${matchCount} match` : `${map?.links?.length || 0} arrows`}
        </span>
      </div>

      <div className="im-tab" style={{ padding: 16 }}>
        {/* ───── Off-map contacts drawer ───── */}
        {drawerCollapsed ? (
          <button
            type="button"
            className="im-drawer-rail"
            onClick={() => setDrawerCollapsed(false)}
            title="Show account contacts"
            aria-label="Show account contacts"
          >
            <ChevronRight size={16} />
            <Users size={16} />
            {offMapContacts.length > 0 && (
              <span className="im-drawer-rail-count">{offMapContacts.length}</span>
            )}
          </button>
        ) : (
          <div className="im-drawer">
            <div className="im-drawer-head">
              <h4>Account contacts</h4>
              <button
                type="button"
                className="im-drawer-collapse"
                onClick={() => setDrawerCollapsed(true)}
                title="Minimize contact list"
                aria-label="Minimize contact list"
              >
                <ChevronLeft size={16} />
              </button>
            </div>
            {offMapContacts.length === 0 ? (
              <div className="im-drawer-empty">
                {(account?.contacts?.length || 0) === 0
                  ? 'No contacts yet — add some from the Contacts tab.'
                  : 'All active contacts are on the map.'}
              </div>
            ) : (
              offMapContacts.map(c => (
                <div className="im-drawer-row" key={c.id}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="im-drawer-name">{c.name}</div>
                    {c.job_title && <div className="im-drawer-title">{c.job_title}</div>}
                  </div>
                  {canEdit && (
                    <button
                      className="btn btn-primary im-place-btn"
                      onClick={() => placeContact(c)}
                      disabled={saving}
                    >
                      Place
                    </button>
                  )}
                </div>
              ))
            )}
            {canEdit && (
              <button
                type="button"
                className="im-drawer-add"
                onClick={openAddContact}
                disabled={saving}
                title="Add a new contact"
              >
                <UserPlus size={14} /> Add
              </button>
            )}
          </div>
        )}

        {/* ───── Canvas ───── */}
        <div className="im-board-scroll" ref={boardRef}>
          <InfluenceCanvas
            zoom={zoom}
            map={map}
            canEdit={canEdit}
            dimmedIds={dimmedIds}
            onPersonClick={handlePersonClick}
            onMovePerson={handleMovePerson}
            onCreateLink={handleCreateLink}
            onSelectLink={handleSelectLink}
            onRenameGroup={handleRenameGroup}
            onDeleteGroup={handleDeleteGroup}
            onReorderGroupToIndex={handleReorderGroupToIndex}
            onRenameRow={handleRenameRow}
            onDeleteRow={handleDeleteRow}
            onMoveRowOffset={handleMoveRowOffset}
          />
        </div>

        {/* ───── Legend ───── */}
        {!showLegend && (
          <button
            type="button"
            className="im-legend-rail"
            onClick={() => setShowLegend(true)}
            title="Show legend"
            aria-label="Show legend"
          >
            <ChevronLeft size={16} />
            <Info size={16} />
          </button>
        )}
        {showLegend && (
          <div className="im-legend" aria-label="Influence map legend">
            <div className="im-legend-head">
              <span>Legend</span>
              <button
                type="button"
                onClick={() => setShowLegend(false)}
                title="Hide legend"
                aria-label="Hide legend"
              >
                <ChevronRight size={16} />
              </button>
            </div>

            <div className="im-legend-section">
              <h5>Relationship Status</h5>
              <div className="im-legend-row">
                <span className="im-legend-swatch" style={{ background: STATUS_COLORS.green }} />
                <span className="im-legend-text">Good, established trust</span>
              </div>
              <div className="im-legend-row">
                <span className="im-legend-swatch" style={{ background: STATUS_COLORS.yellow }} />
                <span className="im-legend-text">Met, need to establish trust</span>
              </div>
              <div className="im-legend-row">
                <span className="im-legend-swatch" style={{ background: STATUS_COLORS.red }} />
                <span className="im-legend-text">Limited or no relationship</span>
              </div>
            </div>

            <div className="im-legend-section">
              <h5>Disposition (number on card)</h5>
              <div className="im-legend-row">
                <span className="im-legend-code">+2…+5</span>
                <span className="im-legend-text">Supporter / advocate</span>
              </div>
              <div className="im-legend-row">
                <span className="im-legend-code">−1…+1</span>
                <span className="im-legend-text">Neutral / unknown</span>
              </div>
              <div className="im-legend-row">
                <span className="im-legend-code">−2…−5</span>
                <span className="im-legend-text">Detractor / action required</span>
              </div>
            </div>

            <div className="im-legend-section">
              <h5>Influence Role</h5>
              <div className="im-legend-row"><span className="im-legend-code">DM</span><span className="im-legend-text">Decision Maker</span></div>
              <div className="im-legend-row"><span className="im-legend-code">INF</span><span className="im-legend-text">Influences DM</span></div>
              <div className="im-legend-row"><span className="im-legend-code">IM</span><span className="im-legend-text">Implementer</span></div>
              <div className="im-legend-row"><span className="im-legend-code">CO</span><span className="im-legend-text">Coach / consultant</span></div>
              <div className="im-legend-row"><span className="im-legend-code">NI</span><span className="im-legend-text">No influence</span></div>
            </div>

            <div className="im-legend-section">
              <h5>Social Style</h5>
              <div className="im-legend-row"><span className="im-legend-code">DR</span><span className="im-legend-text">Driver (direct/closed)</span></div>
              <div className="im-legend-row"><span className="im-legend-code">EX</span><span className="im-legend-text">Expressive (direct/open)</span></div>
              <div className="im-legend-row"><span className="im-legend-code">AY</span><span className="im-legend-text">Analytic (indirect/closed)</span></div>
              <div className="im-legend-row"><span className="im-legend-code">AM</span><span className="im-legend-text">Amiable (indirect/open)</span></div>
              <div className="im-legend-row"><span className="im-legend-code">UN</span><span className="im-legend-text">Unknown</span></div>
            </div>

            <div className="im-legend-hint">
              Each card shows status colour + disposition number, then the influence
              and social-style codes. Arrows mean "X influences Y" — thicker arrows
              represent stronger influence.
            </div>
          </div>
        )}
      </div>

      {/* ───── Edit person modal (tabbed: Map / Contact) ─────
          The Contact tab reuses the same ContactForm component as the
          Account → Contacts modal, so the contact form is defined in
          exactly one place. Tab 1's relationship_status and disposition
          write through to the contact; Tab 2 edits the contact directly
          via /api/contacts/:id. Both tabs trigger the same refresh
          (loadMap + reloadAccount) so the canvas and the rest of the
          page stay in sync. */}
      <Modal
        isOpen={showEditModal}
        onClose={closeEditModal}
        title={editingPerson?.name ? `Edit ${editingPerson.name}` : 'Edit'}
        size="lg"
      >
        {editingPerson && (
          <>
            {/* Tab nav */}
            <div className="im-tabs" style={{ padding: '12px 20px 0' }}>
              <button
                type="button"
                className={`im-tab-btn ${activeModalTab === 'map' ? 'active' : ''}`}
                onClick={() => setActiveModalTab('map')}
              >
                Map details
              </button>
              <button
                type="button"
                className={`im-tab-btn ${activeModalTab === 'contact' ? 'active' : ''}`}
                onClick={() => editingPerson.contact_id && setActiveModalTab('contact')}
                disabled={!editingPerson.contact_id}
                title={editingPerson.contact_id
                  ? 'Edit the underlying CRM contact'
                  : 'Convert ad-hoc person to a CRM contact first'}
              >
                Contact details
              </button>
            </div>

            {/* ──────── Tab 1: Map ──────── */}
            {activeModalTab === 'map' && (
              <form onSubmit={savePerson}>
                <div className="modal-body">
                  {editingPerson.contact_id ? (
                    <div className="im-tab-banner">
                      Linked to CRM contact <strong>{editingPerson.name}</strong>.
                      Name, title, and other contact-wide details live on the Contact tab.
                    </div>
                  ) : (
                    <div className="grid grid-cols-2">
                      <div className="form-group">
                        <label className="form-label">Name *</label>
                        <input
                          type="text"
                          className="form-input"
                          value={personForm.name || ''}
                          onChange={(e) => setPersonForm({ ...personForm, name: e.target.value })}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Title</label>
                        <input
                          type="text"
                          className="form-input"
                          value={personForm.title || ''}
                          onChange={(e) => setPersonForm({ ...personForm, title: e.target.value })}
                        />
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2">
                    <div className="form-group">
                      <label className="form-label">Swimlane</label>
                      <TypeaheadSelect
                        id="im-swimlane"
                        placeholder="Select or add a swimlane…"
                        items={(map?.groups || []).map(g => ({ id: g, name: g }))}
                        value={personForm.group_name || ''}
                        displayValue={personForm.group_name || ''}
                        onChange={(id, name) => setPersonForm(f => ({ ...f, group_name: name }))}
                        onAddNew={canEdit ? addSwimlaneInline : undefined}
                        showAddNewOption={false}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Row</label>
                      <TypeaheadSelect
                        id="im-row"
                        placeholder="Select or add a row…"
                        items={(map?.rows || []).map(r => ({ id: r, name: r }))}
                        value={(map?.rows || [])[personForm.row_index] || ''}
                        displayValue={(map?.rows || [])[personForm.row_index] || ''}
                        onChange={(id, name) => {
                          const idx = (map?.rows || []).indexOf(name);
                          setPersonForm(f => ({ ...f, row_index: idx >= 0 ? idx : '' }));
                        }}
                        onAddNew={canEdit ? addRowInline : undefined}
                        showAddNewOption={false}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2">
                    <div className="form-group">
                      <label className="form-label">Influence Role</label>
                      <select
                        className="form-select"
                        value={personForm.influence_role || ''}
                        onChange={(e) => setPersonForm({ ...personForm, influence_role: e.target.value })}
                      >
                        {INFLUENCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      {editingPerson.contact_id && personForm._has_influence_override && (
                        <div className="im-override-hint">
                          Per-map override active.
                          <button type="button" onClick={() => clearOverride('influence_role')}>
                            Reset to contact default
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Social Style</label>
                      <select
                        className="form-select"
                        value={personForm.social_style || ''}
                        onChange={(e) => setPersonForm({ ...personForm, social_style: e.target.value })}
                      >
                        {SOCIAL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      {editingPerson.contact_id && personForm._has_social_override && (
                        <div className="im-override-hint">
                          Per-map override active.
                          <button type="button" onClick={() => clearOverride('social_style')}>
                            Reset to contact default
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2">
                    <div className="form-group">
                      <label className="form-label">Relationship Status</label>
                      <select
                        className="form-select"
                        value={personForm.relationship_status || ''}
                        onChange={(e) => setPersonForm({ ...personForm, relationship_status: e.target.value })}
                        disabled={!editingPerson.contact_id}
                      >
                        {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      {!editingPerson.contact_id && (
                        <div className="im-override-hint">Promote to a CRM contact to set status.</div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Disposition (−5 … +5)</label>
                      <input
                        type="number"
                        className="form-input"
                        min="-5"
                        max="5"
                        step="1"
                        value={personForm.disposition === '' || personForm.disposition == null ? '' : personForm.disposition}
                        onChange={(e) => setPersonForm({ ...personForm, disposition: e.target.value })}
                        disabled={!editingPerson.contact_id}
                      />
                      {!editingPerson.contact_id && (
                        <div className="im-override-hint">Promote to a CRM contact to set disposition.</div>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Map notes</label>
                    <textarea
                      className="form-textarea"
                      value={personForm.notes || ''}
                      onChange={(e) => setPersonForm({ ...personForm, notes: e.target.value })}
                      placeholder="What we know about this person on this account's buying committee."
                    />
                    <div className="im-override-hint">
                      These notes are specific to this map. The Contact tab edits the contact's general notes.
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeEditModal}>
                    Cancel
                  </button>
                  <div style={{ flex: 1 }} />
                  {!editingPerson.contact_id && canDo('contacts', 'create') && (
                    <button type="button" className="btn btn-secondary" onClick={promoteAdHoc} disabled={saving} title="Create a CRM contact from this ad-hoc person">
                      <RefreshCw size={14} /> Convert to CRM contact
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => { closeEditModal(); removeFromMap(editingPerson); }}
                    disabled={saving}
                  >
                    <Trash2 size={14} /> Remove from map
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving…' : 'Save map details'}
                  </button>
                </div>
              </form>
            )}

            {/* ──────── Tab 2: Contact ──────── */}
            {activeModalTab === 'contact' && editingPerson.contact_id && contactDraft && (
              <form onSubmit={saveContact}>
                <div className="modal-body">
                  <div className="im-tab-banner">
                    Editing the CRM contact directly. Changes here are visible on the
                    Contacts tab and anywhere else this contact appears.
                  </div>
                  <ContactForm
                    contactForm={contactDraft}
                    setContactForm={setContactDraft}
                    leadSources={leadSources}
                    repFirms={repFirms}
                    users={users}
                  />
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeEditModal}>
                    Cancel
                  </button>
                  <div style={{ flex: 1 }} />
                  <button type="submit" className="btn btn-primary" disabled={saving || !canEdit}>
                    {saving ? 'Saving…' : 'Save contact'}
                  </button>
                </div>
              </form>
            )}
          </>
        )}
      </Modal>

      {/* ───── Add contact modal ─────
          Creates a real CRM contact on this account (shared ContactForm),
          then places it on the map in the Unassigned column. */}
      <Modal
        isOpen={showAddContactModal}
        onClose={closeAddContact}
        title="Add contact"
        size="lg"
      >
        {newContactForm && (
          <form onSubmit={saveNewContact}>
            <div className="modal-body">
              <div className="im-tab-banner">
                Creates a new contact on <strong>{account?.name}</strong> and places it on
                the map. It will appear on the Contacts tab like any other contact.
              </div>
              <ContactForm
                contactForm={newContactForm}
                setContactForm={setNewContactForm}
                leadSources={leadSources}
                repFirms={repFirms}
                users={users}
              />
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={closeAddContact}>
                Cancel
              </button>
              <div style={{ flex: 1 }} />
              <button type="submit" className="btn btn-primary" disabled={saving || !newContactForm.first_name?.trim()}>
                {saving ? 'Saving…' : 'Save contact'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      {/* ───── Arrow editor modal ───── */}
      <Modal
        isOpen={!!editingLink}
        onClose={() => setEditingLink(null)}
        title="Influence arrow"
        size="sm"
      >
        {editingLink && (
          <>
            <div className="modal-body">
              <div style={{ fontSize: 13, marginBottom: 12 }}>
                <strong>{linkSrc?.name || '?'}</strong>
                {' → '}
                <strong>{linkTgt?.name || '?'}</strong>
              </div>
              <div className="form-group">
                <label className="form-label">Strength</label>
                <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
                  {[1, 2, 3].map(s => (
                    <button
                      key={s}
                      type="button"
                      className={`btn btn-sm ${editingLink.strength === s ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => updateLinkStrength(s)}
                      disabled={saving || !canEdit}
                    >
                      {s === 1 ? 'Weak' : s === 2 ? 'Moderate' : 'Strong'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Color</label>
                <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
                  {Object.entries(ARROW_COLORS).map(([key, hex]) => {
                    const active = linkColorKey(editingLink) === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        title={key.charAt(0).toUpperCase() + key.slice(1)}
                        onClick={() => updateLinkColor(key)}
                        disabled={saving || !canEdit}
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          background: hex,
                          cursor: saving || !canEdit ? 'default' : 'pointer',
                          border: active ? '3px solid #1e293b' : '3px solid transparent',
                          boxShadow: active ? '0 0 0 1px #fff inset' : 'none',
                          padding: 0,
                        }}
                      />
                    );
                  })}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Direction</label>
                <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className={`btn btn-sm ${!editingLink.bidirectional ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => updateLinkBidirectional(false)}
                    disabled={saving || !canEdit}
                  >
                    One-way →
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${editingLink.bidirectional ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => updateLinkBidirectional(true)}
                    disabled={saving || !canEdit}
                  >
                    Both ways ↔
                  </button>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Curve</label>
                <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className={`btn btn-sm ${!editingLink.curve_flipped ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => updateLinkCurveFlipped(false)}
                    disabled={saving || !canEdit}
                  >
                    Curve ↶
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${editingLink.curve_flipped ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => updateLinkCurveFlipped(true)}
                    disabled={saving || !canEdit}
                  >
                    Flip ↷
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setEditingLink(null)}>
                Close
              </button>
              <div style={{ flex: 1 }} />
              {canEdit && (
                <>
                  <button type="button" className="btn btn-secondary" onClick={flipLink} disabled={saving}>
                    <ArrowLeftRight size={14} /> Flip direction
                  </button>
                  <button type="button" className="btn btn-danger" onClick={deleteLink} disabled={saving}>
                    <Trash2 size={14} /> Delete
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

export default AccountInfluenceMapTab;
